/** Browser geolocation → country/city (no API key). */

export type DetectedLocation = {
  country: string;
  city?: string;
};

const CACHE_KEY = "skutopia_detected_location";
const CACHE_MS = 6 * 60 * 60 * 1000;

function readCache(): DetectedLocation | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at: number; location: DetectedLocation };
    if (!parsed?.location?.country) return null;
    if (Date.now() - parsed.at > CACHE_MS) return null;
    return parsed.location;
  } catch {
    return null;
  }
}

function writeCache(location: DetectedLocation) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), location }));
  } catch {
    /* ignore */
  }
}

function getPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation unavailable"));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 12_000,
      maximumAge: 600_000,
    });
  });
}

export async function detectUserLocation(): Promise<DetectedLocation | null> {
  const cached = readCache();
  if (cached) return cached;

  try {
    const pos = await getPosition();
    const { latitude, longitude } = pos.coords;
    const url =
      `https://api.bigdatacloud.net/data/reverse-geocode-client` +
      `?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = (await res.json()) as {
      countryName?: string;
      city?: string;
      locality?: string;
      principalSubdivision?: string;
    };
    const country = (data.countryName || "").trim();
    if (!country) return null;
    const city = (data.city || data.locality || data.principalSubdivision || "").trim();
    const location: DetectedLocation = { country, city: city || undefined };
    writeCache(location);
    return location;
  } catch {
    return null;
  }
}

/** Sync read of last detected location (for header). */
export function peekDetectedLocation(): DetectedLocation | null {
  return readCache();
}
